import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar } from '@mui/material';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import cssStyles from '../../../utils/cssStyles';
import { HEADER, NAVBAR } from '../../../config';
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
import LanguagePopover from './LanguagePopover';
import AccountPopover from './AccountPopover';
import { getDateDiffInDays } from 'utils/getDateDiffInDays';
import { useTranslation } from 'react-i18next';
import { usePortalListing } from 'api/ApiHooks/GeneralHooks';
import usePortalId from 'hooks/portal/usePortalId';
import { useEffect, useState } from 'react';
import axiosInstance from 'utils/axios';
import { TRIAL_EXTEND_API_URL } from 'api/ApiConstants/DiscountCouponsApiConstants';

type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
  verticalLayout: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: prop =>
    prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})<RootStyleProps>(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
  verticalLayout?: boolean;
};

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
}: Props) {
  const isOffset =
    useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const portalId = usePortalId();
  const { data, refetch } = usePortalListing();
  const activePortal = data?.data.filter(function (portal) {
    return portal.portal_id.toString() === portalId;
  });
  const [isTrialExtended, setIsTrialExtended] = useState(true);

  const { t } = useTranslation();

  const isDesktop = useResponsive('up', 'lg');
  const days = activePortal?.length
    ? getDateDiffInDays(
        new Date(
          +activePortal[0]?.connected_apps[0]?.payment_plan.validity
        ).getTime()
      )
    : 0;

  const extendPlanRequest = async () => {
    try {
      const response = await axiosInstance.get(
        `${TRIAL_EXTEND_API_URL}?portal_id=${portalId}&app_name=automatedcoupon`
      );
      if (response.data.success) {
        setIsTrialExtended(true);
        window.location.reload();
      }
    } catch (error) {
    } finally {
    }
  };

  useEffect(() => {
    if (
      activePortal?.length &&
      days < 0 &&
      !activePortal[0]?.extendTrialPeriodRequest
    ) {
      setIsTrialExtended(false);
    } else if (
      activePortal?.length &&
      days <= 7 &&
      !activePortal[0]?.extendTrialPeriodRequest
    ) {
      setIsTrialExtended(false);
    }
  }, [activePortal, days]);

  return (
    <RootStyle
      isCollapse={isCollapse}
      isOffset={isOffset}
      verticalLayout={verticalLayout}
    >
      {!isTrialExtended ? (
        <div
          style={{
            background: '#3881f7',
            textAlign: 'center',
            padding: '10px',
            color: '#fff',
            fontWeight: '600',
            fontSize: '16px',
          }}
        >
          {days < 0
            ? 'Your validity expired, want to'
            : `Your Validity will expire in ${days} days, want to`}{' '}
          <button
            style={{
              color: '#fff',
              background: 'none',
              padding: 0,
              border: 'none',
              outline: 'none',
              fontWeight: '600',
              fontSize: '16px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={() => {
              extendPlanRequest();
            }}
          >
            Extend
          </button>
        </div>
      ) : (
        <div
          style={{
            background: '#3881f7',
            textAlign: 'center',
            padding: '10px',
            color: '#fff',
            fontWeight: '600',
            fontSize: '16px',
          }}
        >
          {t('category_feature_banner')}{' '}
          <a
            href='https://docs.makewebbetter.com/hubspot-automated-coupons/#generate-coupons-without-hubspot-workflows'
            target='_blank'
            rel='noreferrer'
            style={{
              color: '#1e314f',
            }}
          >
            {t('setup_guide')}
          </a>{' '}
          {t('enable_new_app_card')}
        </div>
      )}{' '}
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

        {!isDesktop && (
          <IconButtonAnimate
            onClick={onOpenSidebar}
            sx={{ mr: 1, color: 'text.primary' }}
          >
            <Iconify icon='eva:menu-2-fill' />
          </IconButtonAnimate>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Stack
          direction='row'
          alignItems='center'
          spacing={{ xs: 0.5, sm: 1.5 }}
        >
          <LanguagePopover />
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
